import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, errText } from "@/lib/api";
import { landingPath } from "@/lib/landing";
import { setSessionToken } from "@/lib/authToken";
import { startLogin } from "@/lib/startLogin";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { applyUser } = useAuth();
  const hasProcessed = useRef(false);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const sessionId = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    const finish = async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        // kept on the device as well as in the cookie — iPad Safari drops cookies on some settings
        if (data.session_token) setSessionToken(data.session_token);
        window.history.replaceState({}, document.title, window.location.pathname);
        await applyUser(data.user);
        navigate(landingPath(data.user?.preferences), { replace: true, state: { user: data.user } });
      } catch (e) {
        window.history.replaceState({}, document.title, window.location.pathname);
        setFailure(errText(e));
      }
    };
    finish();
  }, [location.hash, navigate, applyUser]);

  if (failure) {
    return (
      <div
        data-testid="auth-callback-error"
        className="min-h-screen flex items-center justify-center bg-background px-6"
      >
        <div className="surface p-8 max-w-lg w-full">
          <p className="label-caps">Sign-in did not complete</p>
          <h1 className="font-heading text-2xl font-bold mt-2">Could not open the diary</h1>
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed" data-testid="auth-error-detail">
            {failure}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button
              data-testid="auth-retry-btn"
              onClick={startLogin}
              className="rounded-full h-11 px-6 text-sm font-semibold"
            >
              Try signing in again
            </Button>
            <Button
              variant="outline"
              data-testid="auth-back-btn"
              onClick={() => navigate("/", { replace: true })}
              className="rounded-full h-11 px-5 text-sm"
            >
              Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="auth-callback-screen"
      className="min-h-screen flex items-center justify-center bg-background"
    >
      <p className="font-heading text-xl tracking-tight text-foreground">Opening your diary…</p>
    </div>
  );
}
